import { DEVICE_ABNORMAL_MESSAGE_KEY } from '@src/utils/punch/deviceAbnormality';

// 打卡機 onboarding 返回：暫存最初打卡機入口 URL 的 localStorage key（單一來源）。
// 寫入：business/login（未登入掃碼→登入）或 redirectToRegistrationOnboardingIfNeeded（已登入但 registering）。
// 讀取：OnboardingCompleteStep 完成頁「前往打卡」CTA，導回最初打卡機入口。
export const PUNCH_MACHINE_CB_KEY = 'punchMachineCbAfterOnboarding';

// 打卡機路由：
//   /hr/punch-in/machine      — 真正的掃碼打卡入口
//   /demo/punch-in-machine — 純展示用的「打卡成功」假畫面（免登入、不打 API）
// 兩者共用「無版面 + 不抓 theme」的極簡 render 路徑，_app / AppBackground 用 isPunchMachinePath 判斷。
export const PUNCH_MACHINE_PATH = '/hr/punch-in/machine';
export const PUNCH_MACHINE_DEMO_PATH = '/demo/punch-in-machine';

export const isPunchMachinePath = (pathname?: string | null): boolean => (
	pathname === PUNCH_MACHINE_PATH || pathname === PUNCH_MACHINE_DEMO_PATH
);

export const PUNCH_ERROR_TYPE = {
	MACHINE_INVALID: 'machineInvalid', // 找不到對應打卡機／尚未綁定學校
	MACHINE_NOT_ACTIVATED: 'machineNotActivated', // 打卡機尚未開通
	MACHINE_DISABLED: 'machineDisabled', // 打卡機已停用
	NO_RULE: 'noRule', // 未選擇出勤規則
	NO_SHIFT_SCHEDULED: 'noShiftScheduled', // 當日無排班
	NO_PERMISSION: 'noPermission', // 無權限使用此打卡機
	COUNTER_INVALID: 'counterInvalid', // 讀取計數異常（他人使用／自己用過／過舊）→ 請重新感應
	DEVICE_ABNORMAL: 'deviceAbnormal', // 進階防代打卡：裝置已綁定他人／超出裝置數量上限
	DEFAULT: 'default', // 打卡失敗（含 'invalid url' 及其他未列舉的 error_type）
} as const;

export type PunchErrorType = typeof PUNCH_ERROR_TYPE[keyof typeof PUNCH_ERROR_TYPE];

// key 必為小寫（mapPunchErrorToType 會把 error_type 先 toLowerCase）
// 'invalid url' 與其他未列舉者 → DEFAULT；
const ERROR_TYPE_TO_TYPE: Record<string, PunchErrorType> = {
	'machine not found': PUNCH_ERROR_TYPE.MACHINE_INVALID, // 找不到對應打卡機
	'machine is not bound': PUNCH_ERROR_TYPE.MACHINE_INVALID, // 打卡機尚未綁定學校
	'bound school not found': PUNCH_ERROR_TYPE.MACHINE_NOT_ACTIVATED, // 打卡機尚未開通
	'machine is not enabled': PUNCH_ERROR_TYPE.MACHINE_DISABLED, // 打卡機已停用
	'punch in rule set not found': PUNCH_ERROR_TYPE.NO_RULE, // 未選擇出勤規則
	'no shift scheduled': PUNCH_ERROR_TYPE.NO_SHIFT_SCHEDULED, // 當日無排班
	'time_clock not allow': PUNCH_ERROR_TYPE.NO_PERMISSION, // 無權限使用此打卡機
	'user mismatch for the same time clock': PUNCH_ERROR_TYPE.COUNTER_INVALID, // 讀取計數已由其他使用者使用
	'the same user already used this time clock for the same counter': PUNCH_ERROR_TYPE.COUNTER_INVALID, // 讀取計數已由同一位使用者使用過
	'stale time clock counter': PUNCH_ERROR_TYPE.COUNTER_INVALID, // 讀取計數過舊
	other_user_device: PUNCH_ERROR_TYPE.DEVICE_ABNORMAL, // 此裝置已綁定其他使用者
	exceed_allowed_device_count: PUNCH_ERROR_TYPE.DEVICE_ABNORMAL, // 已達允許綁定的裝置數量上限
};

export const mapPunchErrorToType = (err: unknown): PunchErrorType => {
	const data = (err as { response?: { data?: { error_type?: string } } })?.response?.data;
	const key = (data?.error_type || '').trim().toLowerCase();
	return ERROR_TYPE_TO_TYPE[key] || PUNCH_ERROR_TYPE.DEFAULT;
};

interface IVariant {
	titleKey: string;
	descKey?: string;
}

// 各錯誤類型對應的彈窗標題／說明 i18n key（ErrorResult 使用）。
export const PUNCH_ERROR_VARIANTS: Record<PunchErrorType, IVariant> = {
	[PUNCH_ERROR_TYPE.MACHINE_NOT_ACTIVATED]: {
		titleKey: 'hr:attendance:punch_machine:not_activated_title',
		descKey: 'hr:attendance:punch_machine:not_activated_desc',
	},
	[PUNCH_ERROR_TYPE.MACHINE_INVALID]: {
		titleKey: 'hr:attendance:punch_machine:fail_title',
		descKey: 'hr:attendance:punch_machine:error_occurred_desc',
	},
	[PUNCH_ERROR_TYPE.MACHINE_DISABLED]: {
		titleKey: 'hr:attendance:punch_machine:disabled_title',
		descKey: 'hr:attendance:punch_machine:disabled_desc',
	},
	[PUNCH_ERROR_TYPE.NO_RULE]: {
		titleKey: 'hr:attendance:punch_machine:no_rule_title',
		descKey: 'hr:attendance:punch_machine:contact_admin',
	},
	[PUNCH_ERROR_TYPE.NO_SHIFT_SCHEDULED]: {
		titleKey: 'hr:attendance:punch_machine:fail_title',
		descKey: 'hr:attendance:punch_machine:no_shift_scheduled_desc',
	},
	[PUNCH_ERROR_TYPE.NO_PERMISSION]: {
		titleKey: 'hr:attendance:punch_machine:no_permission_title',
		descKey: 'hr:attendance:punch_machine:contact_admin',
	},
	[PUNCH_ERROR_TYPE.COUNTER_INVALID]: {
		titleKey: 'hr:attendance:punch_machine:fail_title',
		descKey: 'hr:attendance:punch_machine:rescan_desc',
	},
	[PUNCH_ERROR_TYPE.DEVICE_ABNORMAL]: {
		titleKey: 'hr:attendance:punch_machine:fail_title',
		descKey: DEVICE_ABNORMAL_MESSAGE_KEY,
	},
	[PUNCH_ERROR_TYPE.DEFAULT]: {
		titleKey: 'hr:attendance:punch_machine:fail_title',
		descKey: 'hr:attendance:punch_machine:default_desc',
	},
};
